import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "../slugs/vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const sClassWithChauffeur: ChauffeurPage = {
  slug: "s-class-with-chauffeur",
  title: "Mercedes S-Class with chauffeur — prestige sedan",
  subtitle:
    "The benchmark limousine with private chauffeur: prestige, silence and lounge comfort across the French Riviera.",
  keywords: [
    "Mercedes S-Class with chauffeur",
    "luxury sedan with chauffeur Cannes",
    "limousine with chauffeur French Riviera",
    "luxury car service Nice",
  ],
  hero: {
    title: "Mercedes S-Class with chauffeur on the French Riviera",
    subtitle:
      "The prestige sedan par excellence: lounge comfort in the back, absolute silence and an exceptional chauffeur for demanding clients.",
    keywords: ["S-Class with chauffeur", "luxury sedan", "limousine"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes S-Class with private chauffeur in Cannes",
    badges: ["Up to 3 passengers", "Lounge comfort", "Prestige"],
  },
  benefits: {
    heading: "Why the S-Class with chauffeur from KS Rent Services",
    items: [
      {
        title: "Absolute prestige",
        description:
          "The benchmark of luxury sedans: a presence and refinement that command attention, from the red carpet to the exceptional appointment.",
      },
      {
        title: "Lounge comfort",
        description:
          "Enveloping rear seats, hushed ride and high-end finishes: a true lounge in motion.",
      },
      {
        title: "Exceptional chauffeur",
        description:
          "Discretion, anticipation and attention to detail: the welcome and service that demanding clients deserve.",
      },
      {
        title: "A noticed arrival",
        description:
          "Make every entrance count: the S-Class leaves an impression at your events and receptions.",
      },
    ],
  },
  services: {
    heading: "Prestige sedan services",
    services: [
      {
        title: "Festival & red carpet",
        text: "Stair climbs, premieres and events: an arrival worthy of the occasion.",
      },
      {
        title: "Galas & receptions",
        text: "Dinners, galas and private evenings across the French Riviera, with a dedicated chauffeur at your disposal.",
      },
      {
        title: "VIP transfers",
        text: "Nice airport, Monaco heliport, hotels and residences: high-end, confidential transfers.",
      },
      {
        title: "Weddings & hourly hire",
        text: "A prestige procession or a chauffeur by the day for your finest occasions.",
      },
    ],
  },
  locations: {
    heading: "Areas served",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & airport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "The pinnacle of the sedan with chauffeur",
    subheading: "When the journey is already part of the experience.",
    paragraphs: [
      "The Mercedes S-Class embodies automotive luxury: a cabin designed like a lounge, where every detail serves your comfort and serenity.",
      "For your events, receptions and VIP travel, it combines visible prestige with absolute discretion.",
      "A single point of contact, an exceptional chauffeur and the same punctuality standard as across our whole fleet.",
    ],
    bullets: [
      "Prestige sedan for up to 3 passengers",
      "Transparent airport packages",
      "Available 24/7",
    ],
  },
  process: {
    heading: "How to book your S-Class with chauffeur",
    steps: [
      "Tell us your route, your event and your schedule.",
      "Receive a clear package or a per-kilometre estimate.",
      "Assignment of the S-Class and your exceptional chauffeur.",
      "Prestige, punctual and confidential pick-up.",
    ],
  },
  faq: [
    {
      q: "Which vehicle is the prestige sedan?",
      a: "A Mercedes S-Class, the benchmark of luxury sedans, for up to 3 passengers, ideal for events and VIP travel.",
    },
    {
      q: "What is the difference with the E-Class?",
      a: "The S-Class offers a superior experience — lounge comfort, prestige and presence — where the E-Class remains the business sedan at the best service-to-price ratio.",
    },
    {
      q: "Is it suited to the Cannes Film Festival and galas?",
      a: "Absolutely: it is the vehicle of noticed arrivals, red-carpet stair climbs and prestige evenings.",
    },
    vehiclePriceFaq("en", "CLASS_S", "an S-Class with chauffeur"),
    {
      q: "How do I book?",
      a: "Tell us your route and your occasion: we quickly confirm the S-Class and your dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Sedan with chauffeur (E-Class)",
      href: "/en/chauffeur/sedan-with-chauffeur",
    },
    {
      label: "Range Rover with chauffeur (SUV)",
      href: "/en/chauffeur/suv-with-chauffeur",
    },
    { label: "All our chauffeur services", href: "/en/chauffeur" },
    {
      label: "Wedding motorcade in Cannes",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "An exceptional occasion to honour?",
    text: "Tell us your route — fast quote for your Mercedes S-Class with chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Request a quote",
  },
  seo: {
    ...vehicleSeo(
      "en",
      "CLASS_S",
      "Mercedes S-Class with chauffeur, French Riviera",
      "Mercedes S-Class with chauffeur",
    ),
    canonical: "/en/chauffeur/s-class-with-chauffeur",
  },
};

export default sClassWithChauffeur;
